import React, { Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { PageLayout } from '@/components/PageLayout';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { AppTopBar } from '@/components/layout/AppTopBar';
import { SessionRefreshBanner } from '@/components/layout/SessionUnreachable';
import { PageTopBarProvider } from '@/components/layout/PageTopBarContext';
import { TaskLiveConnector } from '@/components/tasks/TaskLiveConnector';
import { TasksPanelHost } from '@/components/tasks/TasksPanel';
import { TaskSavingIndicator } from '@/components/tasks/TaskSavingIndicator';
import { UnsavedChangesNavigator } from '@/hooks/useUnsavedChangesGuard';
import { useInboxPushNavigation } from '@/components/inbox/use-inbox';
import { useActivityTracking } from '@/hooks/use-activity-tracking';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { lazyPage } from '@/lib/lazy-page';
import { LoadingState } from '@/components/LoadingState';
import { useChatPanelLayout, useChatPanelShown } from '@/pages/chat/chat-panel-state';
import { ChatDockFrame } from '@/pages/chat/ChatDockFrame';
import { LinkDialogHost } from '@/pages/link-dialog/LinkDialogHost';

// Loaded when it first opens: most pages never show it.
const ChatDock = lazyPage(() => import('@/pages/chat/ChatDock'));

/**
 * Shell for signed-in pages: the searchable sidebar, the sticky top bar with breadcrumbs and the
 * account menu, the current project's live socket and Tasks panel, the chat panel (admins), the
 * count of the person's active minutes (T-102), and the dialogs links in the chat and task comments
 * open in (SF-T-160).
 */
export function AppLayout({ children }: { children: React.ReactNode }) {
  useInboxPushNavigation();
  // People's time (T-102): this tab's active minutes.
  useActivityTracking();
  const { pathname } = useLocation();
  const chatShown = useChatPanelShown(pathname);
  // On a phone the chat panel covers the screen: what's behind it can't be focused or read (a dialog).
  const covered = useChatPanelLayout() === 'cover' && chatShown;
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <PageTopBarProvider>
          <UnsavedChangesNavigator />
          <TaskLiveConnector />
          <div className="contents" inert={covered}>
            <AppTopBar />
          </div>
          {/* The page, and the chat panel beside it when it's open (T-78): the page narrows to make room. */}
          <div className="flex min-w-0 flex-1">
            <div className="contents" inert={covered}>
              <PageLayout className="flex-1" banner={<SessionRefreshBanner />}>
                {children}
              </PageLayout>
            </div>
            {chatShown ? <ChatDockHost /> : null}
          </div>
          <TasksPanelHost />
          <TaskSavingIndicator />
          {/* Links in the chat and task comments open in dialogs over the page (SF-T-160). */}
          <LinkDialogHost />
        </PageTopBarProvider>
      </SidebarInset>
    </SidebarProvider>
  );
}

/** The chat panel (admins, while it's open, on every page but Chat's own). */
function ChatDockHost() {
  return (
    <ChatDockFrame>
      <ErrorBoundary variant="section" title="The chat panel stopped working">
        <Suspense fallback={<LoadingState label="Loading chat…" className="py-16" />}>
          <ChatDock />
        </Suspense>
      </ErrorBoundary>
    </ChatDockFrame>
  );
}
